import { CARCASS_MM } from "./carcass";
import type {
  EndModuleShape,
  EndModuleSide,
  PlannerProject,
  WardrobeModule
} from "../types";
import { installationSpanMm } from "./installation";
import { cornerReturnWidth, cornerRun, cornerSide } from "./corner";
import {
  BACK_WALL_LENGTH_MM,
  materializeWallOffsets,
  moduleOverlapsFloor,
  PERPENDICULAR_WALL_CLEARANCE_MM
} from "./layout";

export const END_MODULE_MIN_WIDTH_MM = 180;
export const END_MODULE_MAX_WIDTH_MM = 600;
export const END_MODULE_MIN_DEPTH_MM = 200;
export const END_MODULE_MAX_DEPTH_MM = 700;
export const END_MODULE_MIN_TIERS = 2;
export const END_MODULE_MAX_TIERS = 8;

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

export const endModuleSide = (module: WardrobeModule): EndModuleSide =>
  module.endSide === "left" ? "left" : "right";

export const endModuleShape = (module: WardrobeModule): EndModuleShape =>
  module.endShape === "beveled" ? "beveled" : "rounded";

export const endModuleTierCount = (module: WardrobeModule) =>
  clamp(
    Math.round(module.endShelfCount ?? 5),
    END_MODULE_MIN_TIERS,
    END_MODULE_MAX_TIERS
  );

/**
 * Контур горизонтальной панели торца в плане, мм: x от центра, z от фасада
 * (0) к стене (−глубина). Тот же контур строит 3D; по нему же площадь в смете.
 */
/** Задник торца, мм: панели стоят перед ним, а не сквозь него. */
export const END_MODULE_BACK_MM = 10;

export const endModulePlateOutlineMm = (module: WardrobeModule): Array<[number, number]> => {
  const width = module.width;
  const depth = module.depth;
  const sign = endModuleSide(module) === "left" ? 1 : -1;
  // Для левого торца: примыкающая сторона справа (+x), скругление слева.
  // Панель — между примыкающей боковиной и краем, перед задником: раньше она
  // шла сквозь боковину и задник, и сзади были видны её рёбра (приёмка 08.10).
  const near = (sign * width) / 2 - sign * CARCASS_MM;
  const far = (-sign * width) / 2;
  const back = -depth + END_MODULE_BACK_MM;
  const points: Array<[number, number]> = [[far, back], [near, back], [near, 0]];
  if (endModuleShape(module) === "rounded") {
    const p0: [number, number] = [near, 0];
    const p1: [number, number] = [sign * width * 0.08, 0];
    const p2: [number, number] = [far, -depth * 0.24];
    const p3: [number, number] = [far, -depth * 0.56];
    const segments = 20;
    for (let index = 1; index <= segments; index += 1) {
      const t = index / segments;
      const u = 1 - t;
      const a = u * u * u;
      const b = 3 * u * u * t;
      const c = 3 * u * t * t;
      const d = t * t * t;
      points.push([
        a * p0[0] + b * p1[0] + c * p2[0] + d * p3[0],
        a * p0[1] + b * p1[1] + c * p2[1] + d * p3[1]
      ]);
    }
  } else {
    points.push([far, -depth * 0.48]);
  }
  return points;
};

/** Площадь одной горизонтальной панели торца по её фигурному контуру, м². */
export const endModulePlateAreaM2 = (module: WardrobeModule) => {
  const points = endModulePlateOutlineMm(module);
  return Math.abs(points.reduce((sum, point, index) => {
    const next = points[(index + 1) % points.length]!;
    return sum + point[0] * next[1] - next[0] * point[1];
  }, 0)) / 2_000_000;
};

export const endModuleDescription = (module: WardrobeModule) =>
  `${endModuleSide(module) === "left" ? "левый" : "правый"}, ${
    endModuleShape(module) === "rounded" ? "радиусный" : "скошенный"
  }, ${endModuleTierCount(module)} ярусов`;

export const isEndModuleAttachmentTarget = (module: WardrobeModule) =>
  module.type === "single" || module.type === "double" || module.type === "corner45";

export const endModuleAttachmentTargets = (project: PlannerProject) =>
  project.modules.filter(isEndModuleAttachmentTarget);

export const boundaryCoreModule = (
  project: PlannerProject,
  side: EndModuleSide
) => {
  const coreModules = endModuleAttachmentTargets(project);
  if (!coreModules.length) return undefined;
  const offsets = materializeWallOffsets(project);
  return coreModules.reduce((boundary, candidate) => {
    const boundaryOffset = offsets.get(boundary.id) ?? 0;
    const candidateOffset = offsets.get(candidate.id) ?? 0;
    if (side === "left") {
      return candidateOffset < boundaryOffset ? candidate : boundary;
    }
    const boundaryEdge = boundaryOffset + installationSpanMm(boundary);
    const candidateEdge = candidateOffset + installationSpanMm(candidate);
    return candidateEdge > boundaryEdge ? candidate : boundary;
  });
};

/** Explicit attachment wins; legacy projects fall back to the old outer-edge rule. */
export const endModuleTarget = (
  project: PlannerProject,
  module: WardrobeModule
) =>
  project.modules.find(
    (candidate) =>
      candidate.id === module.endAttachedToId &&
      isEndModuleAttachmentTarget(candidate)
  ) ?? boundaryCoreModule(project, endModuleSide(module));

/** A composition has one physical end on the left and one on the right. */
export const endModuleForSide = (
  project: PlannerProject,
  side: EndModuleSide,
  excludedModuleId?: string
) =>
  project.modules.find(
    (module) =>
      module.type === "end" &&
      module.id !== excludedModuleId &&
      endModuleSide(module) === side
  );

/**
 * Торец этого корпуса с этой стороны. У каждого шкафа свой левый и свой
 * правый торец; раньше торец с каждой стороны был один на весь проект, и
 * у второго шкафа его было не поставить (приёмка 07.10, wb-24).
 */
export const endModuleForTargetSide = (
  project: PlannerProject,
  targetId: string,
  side: EndModuleSide,
  excludedModuleId?: string
) =>
  project.modules.find(
    (module) =>
      module.type === "end" &&
      module.id !== excludedModuleId &&
      endModuleSide(module) === side &&
      endModuleTarget(project, module)?.id === targetId
  );

/**
 * Угловой (28.10): торец — только у наружного торца корпуса, где дверь. С
 * другой стороны — глухая часть, перед ней встаёт соседний шкаф.
 */
export const cornerEndSide = (corner: WardrobeModule): EndModuleSide => {
  const left = cornerSide(corner) === "left";
  return cornerRun(corner) === "back" ? left ? "right" : "left" : left ? "left" : "right";
};

/**
 * Край корпуса на линии стен, к которому встаёт торец. Угловой у боковой
 * стены стоит своим корпусом на ней: его наружный торец — в конце плеча по
 * боковой стене, а не по задней.
 */
const targetEdge = (target: WardrobeModule, offset: number, side: EndModuleSide) => {
  if (target.type === "corner45" && cornerRun(target) === "side" && side === cornerEndSide(target)) {
    const length = cornerReturnWidth(target);
    return side === "left"
      ? -length - PERPENDICULAR_WALL_CLEARANCE_MM
      : BACK_WALL_LENGTH_MM + PERPENDICULAR_WALL_CLEARANCE_MM + length;
  }
  return side === "left" ? offset : offset + installationSpanMm(target);
};

export const endModuleAttachmentOffset = (
  project: PlannerProject,
  module: WardrobeModule,
  target: WardrobeModule,
  side: EndModuleSide = endModuleSide(module)
) => {
  const offsets = materializeWallOffsets({
    ...project,
    modules: project.modules.filter((candidate) => candidate.id !== module.id)
  });
  const edge = targetEdge(target, offsets.get(target.id) ?? 0, side);
  return side === "left"
    ? edge - installationSpanMm(module) - 1
    : edge + 1;
};

/**
 * Checks the real room footprint, not just the order in the module list.
 * This matters at a corner: the inner side of a cabinet can already be
 * occupied by the perpendicular wing of a corner module.
 */
export const canAttachEndModule = (
  project: PlannerProject,
  module: WardrobeModule,
  target: WardrobeModule,
  side: EndModuleSide = endModuleSide(module)
) => {
  if (!isEndModuleAttachmentTarget(target)) return false;
  if (target.type === "corner45" && side !== cornerEndSide(target)) return false;
  const modulesWithoutEnd = project.modules.filter(
    (candidate) => candidate.id !== module.id
  );
  const projectWithoutEnd = { ...project, modules: modulesWithoutEnd };
  const currentOffsets = materializeWallOffsets(projectWithoutEnd);
  // Freeze the current composition before adding the candidate. Otherwise an
  // implicitly centred cabinet would be centred again together with the new
  // end module and appear to collide with it during this preview check.
  const positionedModules = modulesWithoutEnd.map((candidate) => ({
    ...candidate,
    wallOffset: currentOffsets.get(candidate.id) ?? candidate.wallOffset
  }));
  const positionedProject = { ...project, modules: positionedModules };
  const positionedTarget = positionedModules.find(
    (candidate) => candidate.id === target.id
  ) ?? target;
  const candidate: WardrobeModule = {
    ...module,
    type: "end",
    endSide: side,
    endAttachedToId: target.id
  };
  candidate.wallOffset = endModuleAttachmentOffset(
    positionedProject,
    candidate,
    positionedTarget,
    side
  );
  return !moduleOverlapsFloor(
    { ...project, modules: [...positionedModules, candidate] },
    candidate.id
  );
};

/**
 * К какому корпусу встанет новый торец: к выбранному пеналу, а если выбран не
 * пенал — к крайнему. Раньше всегда к крайнему, и торец уезжал к другому шкафу
 * комнаты (приёмка 05.10, wb-24).
 */
export const endModuleAddTarget = (project: PlannerProject, side: EndModuleSide) => {
  const selected = project.modules.find((module) => module.id === project.selectedModuleId);
  // Выбран торец — значит, его шкаф; выбрана антресоль — шкаф под ней.
  const base = selected?.type === "end"
    ? endModuleTarget(project, selected)
    : selected?.stackedOnId
      ? project.modules.find((module) => module.id === selected.stackedOnId)
      : selected;
  if (base && isEndModuleAttachmentTarget(base)) return base;
  return boundaryCoreModule(project, side);
};

/** Почему торец с этой стороны сейчас не поставить; null — можно. */
export const endModuleAddBlocker = (project: PlannerProject, side: EndModuleSide): string | null => {
  const target = endModuleAddTarget(project, side);
  if (!target) return "сначала добавьте шкаф";
  if (target.type === "corner45" && side !== cornerEndSide(target)) {
    return "у углового тут глухая часть — ставится соседний шкаф";
  }
  if (endModuleForTargetSide(project, target.id, side)) {
    return side === "left" ? "у этого шкафа левый торец уже есть" : "у этого шкафа правый торец уже есть";
  }
  const probe: WardrobeModule = {
    id: "end-probe", type: "end", width: 320, height: target.height, depth: target.depth,
    door: "open", facade: "wood", handle: "push", items: [], endSide: side
  };
  if (!canAttachEndModule(project, probe, target, side)) {
    return `у выбранного корпуса ${side === "left" ? "слева" : "справа"} сосед или стена`;
  }
  return null;
};

export const syncAttachedEndModuleMaterials = (
  project: PlannerProject,
  sourceModuleId: string,
  material: WardrobeModule["material"]
) => {
  if (!material) return project;
  project.modules.forEach((module) => {
    if (
      module.type === "end" &&
      endModuleTarget(project, module)?.id === sourceModuleId
    ) {
      module.material = material;
    }
  });
  return project;
};

export const alignEndModuleOffsets = (project: PlannerProject) => {
  const coreModules = project.modules.filter(
    (module) => module.type !== "mezzanine" && module.type !== "end"
  );
  if (!coreModules.length) return project;

  const offsets = materializeWallOffsets(project);
  const groups = new Map<string, WardrobeModule[]>();
  project.modules.filter((module) => module.type === "end").forEach((module) => {
    const side = endModuleSide(module);
    // Торец, привязанный к шкафу, всегда остаётся у этого шкафа. Если рядом
    // стало тесно, раздвигаются шкафы вместе с торцами (separateOverlappingFloorModules),
    // а торец не перескакивает к другому шкафу и не пропадает (приёмка 07.10, wb-24).
    const explicitTarget = project.modules.find(
      (candidate) =>
        candidate.id === module.endAttachedToId &&
        isEndModuleAttachmentTarget(candidate)
    );
    // Старые проекты без привязки: крайний шкаф, а если к нему нельзя — любой свободный.
    const target = explicitTarget ?? [
      boundaryCoreModule(project, side),
      ...endModuleAttachmentTargets(project)
    ].filter((candidate, index, candidates): candidate is WardrobeModule =>
      Boolean(candidate) &&
      candidates.findIndex((other) => other?.id === candidate?.id) === index
    ).find((candidate) =>
      canAttachEndModule(project, module, candidate, side)
    );
    if (!target) return;
    module.endAttachedToId = target.id;
    // У углового торец один — у наружного торца; сменили стену углового — торец переехал с ним.
    if (target.type === "corner45") module.endSide = cornerEndSide(target);
    const key = `${target.id}:${endModuleSide(module)}`;
    groups.set(key, [...(groups.get(key) ?? []), module]);
  });
  groups.forEach((ends, key) => {
    const separator = key.lastIndexOf(":");
    const targetId = key.slice(0, separator);
    const side = key.slice(separator + 1) as EndModuleSide;
    const target = coreModules.find((module) => module.id === targetId);
    if (!target) return;
    if (side === "left") {
      let edge = targetEdge(target, offsets.get(target.id) ?? 0, side);
      [...ends]
        .sort((a, b) => (b.wallOffset ?? 0) - (a.wallOffset ?? 0))
        .forEach((module) => {
          module.wallOffset = edge - installationSpanMm(module) - 1;
          edge = module.wallOffset;
        });
    } else {
      let edge = targetEdge(target, offsets.get(target.id) ?? 0, side);
      [...ends]
        .sort((a, b) => (a.wallOffset ?? 0) - (b.wallOffset ?? 0))
        .forEach((module) => {
          module.wallOffset = edge + 1;
          edge = module.wallOffset + installationSpanMm(module);
        });
    }
  });
  return project;
};
